import React, { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";
import api from "../api";
import Logo from "../components/Logo";
import RiskBreakdown from "../components/RiskBreakdown";
import IPIntelligenceCard from "../components/IPIntelligenceCard";
import ReconCard from "../components/ReconCard";
import CVECard from "../components/CVECard";
import ScreenshotCard from "../components/ScreenshotCard";
import ScanAgentChat from "../components/ScanAgentChat";
import CompanyVerifyModal from "../components/CompanyVerifyModal";
import {
  Shield, ArrowRight, AlertTriangle, Lock, Download, Gem,
  Globe, ClipboardList, Plug, Bot, Scale, KeyRound, CheckCircle2, XCircle,
  CalendarClock, Plus, Trash2, Power, Terminal, Layers,
} from "lucide-react";

export default function Scanner() {
  const [params] = useSearchParams();
  const [target, setTarget] = useState(params.get("target") || "");
  const [scanType, setScanType] = useState("full");
  const [deepTools, setDeepTools] = useState(false);
  const [authCookie, setAuthCookie] = useState("");
  const [authBearer, setAuthBearer] = useState("");
  const [pentestMode, setPentestMode] = useState(false);
  const [showAssetCtx, setShowAssetCtx] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [assetCrit, setAssetCrit] = useState("");
  const [bizImpact, setBizImpact] = useState("");
  const [dataClass, setDataClass] = useState("");
  const [pentestAuth, setPentestAuth] = useState(false);
  const [busy, setBusy] = useState(false);
  const [selectedProvider] = useState("groq");
  const [creditBalance, setCreditBalance] = useState(0);
  const [progress, setProgress] = useState(0);
  const [phase, setPhase] = useState("");
  const [result, setResult] = useState(null);
  const [showDisclaimer, setShowDisclaimer] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const [disclaimerText, setDisclaimerText] = useState("");
  const [showCompanyVerify, setShowCompanyVerify] = useState(false);
  const nav = useNavigate();
  const timers = React.useRef([]);
  const clearTimers = () => { timers.current.forEach(clearInterval); timers.current = []; };
  useEffect(() => () => clearTimers(), []);

  const phases = ["Resolving target…", "Checking SSL/TLS…", "DNS inspection…", "Port discovery…", "Header analysis…", "AI analysis…"];

  useEffect(() => {
    api.get("/api/scan/disclaimer").then(r => setDisclaimerText(r.data.disclaimer)).catch(() => {});
    api.get("/api/credits/balance").then(r => setCreditBalance(r.data.credits || 0)).catch(() => {});
  }, []);

  const requestScan = () => {
    if (!target.trim()) { toast.error("Enter domain or IP"); return; }
    if (pentestMode && !pentestAuth) { toast.error("Active pentest requires you to confirm authorization"); return; }
    setShowDisclaimer(true);
    setAgreed(false);
  };

  const startScan = async () => {
    if (!agreed) { toast.error("Please agree to disclaimer"); return; }
    setShowDisclaimer(false);
    setBusy(true); setProgress(0); setResult(null);

    let pi = 0;
    const phaseTimer = setInterval(() => { setPhase(phases[pi % phases.length]); pi++; }, 3000);
    const progTimer = setInterval(() => setProgress(p => p >= 90 ? 90 : p + 1), 800);
    timers.current.push(phaseTimer, progTimer);

    try {
      const res = await api.post("/api/scan/submit", { target: target.trim(), scan_type: scanType, consent: true, ai_provider: selectedProvider, deep_tools: deepTools, auth_cookie: authCookie.trim(), auth_bearer: authBearer.trim(), pentest_mode: pentestMode, pentest_authorized: pentestMode && pentestAuth, asset_criticality: assetCrit, business_impact: bizImpact, data_classification: dataClass });
      const id = res.data.scan_id;

      const poll = setInterval(async () => {
        try {
          const st = await api.get(`/api/scan/status/${id}`);
          if (st.data.status === "done" || st.data.status === "DONE") {
            clearTimers();
            setProgress(100); setPhase("Complete!");
            const full = await api.get(`/api/scan/result/${id}`);
            setResult(full.data); setBusy(false);
            toast.success("Scan complete!");
          } else if (st.data.status === "failed" || st.data.status === "FAILED") {
            clearTimers();
            setBusy(false); toast.error("Scan failed");
          }
        } catch { clearTimers(); setBusy(false); toast.error("Lost connection to scan"); }
      }, 4000);
      timers.current.push(poll);
    } catch (err) {
      clearTimers(); setBusy(false);
      const status = err.response?.status;
      const msg = err.response?.data?.detail || "Scan failed";
      if (status === 403 && msg.includes("COMPANY_VERIFY_REQUIRED")) {
        setShowCompanyVerify(true);
      }
      else if (status === 403) toast.error(msg, { duration: 6000 });
      else if (status === 402) {
        toast.error(msg + " Redirecting to wallet...");
        setTimeout(() => nav("/wallet"), 2000);
      }
      else toast.error(msg);
    }
  };

  const download = async (fmt) => {
    if (!result) return;
    const permKey = fmt === "pdf-standard" ? "pdf" : fmt;
    if (!result.plan_info?.downloads?.includes(permKey)) {
      toast.error(`${permKey.toUpperCase()} downloads require a higher plan`);
      setTimeout(() => nav("/#pricing"), 1500);
      return;
    }
    try {
      const res = await api.get(`/api/reports/${fmt}/${result.scan_id}`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      const ext = fmt === "pdf-standard" ? "pdf" : fmt;
      link.href = url; link.download = `aegis_${result.target}.${ext}`; link.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      const msg = err.response?.data?.detail || "Download failed";
      if (msg.includes("plan")) {
        toast.error(msg, { duration: 5000 });
        setTimeout(() => window.location.href = "/#pricing", 2000);
      } else toast.error("Download failed");
    }
  };

  const rc = result ? (result.risk_score >= 70 ? "var(--au-error)" : result.risk_score >= 40 ? "var(--au-warn)" : "var(--au-accent-ink)") : "var(--au-muted)";
  const planInfo = result?.plan_info || {};
  const isLocked = (val) => val && typeof val === 'object' && val._blocked;

  return (
    <div className="au-root">
      <nav style={S.nav}>
        <Link to="/" style={{textDecoration:"none"}}><Logo size="small" /></Link>
        <div style={{display:"flex",alignItems:"center",gap:18}}>
          <Link to="/dashboard" style={{color:"var(--au-text-2)",textDecoration:"none",fontSize:13,display:"inline-flex",alignItems:"center",gap:6}}><ArrowRight size={14} style={{transform:"rotate(180deg)"}}/> Dashboard</Link>
          <button onClick={() => { localStorage.clear(); sessionStorage.clear(); window.location.replace("/"); }} className="au-pill au-pill-glass au-pill-sm">Logout</button>
        </div>
      </nav>

      <div style={{maxWidth:960,margin:"0 auto",padding:"40px 24px"}}>
        <h1 style={{fontSize:30,fontWeight:700,color:"var(--au-text)",letterSpacing:2,marginBottom:6,display:"flex",alignItems:"center",gap:12}}><Shield size={28} style={{color:"var(--au-accent-ink)"}}/> NEW SCAN</h1>
        <p style={{color:"var(--au-muted)",marginBottom:18}}>External scan - nothing to install on your servers</p>

        <div className="au-glass" style={{padding:"12px 16px",marginBottom:20,display:"flex",alignItems:"flex-start",gap:10}}>
          <AlertTriangle size={16} style={{color:"var(--au-warn)",flex:"none",marginTop:2}}/>
          <span style={{fontSize:13,color:"var(--au-text-2)"}}>
            <strong style={{color:"var(--au-warn)"}}>Legal Notice: </strong>
            Only scan domains you own or have written permission to test. Government, military, education and banking sites are blocked.
          </span>
        </div>

        <div className="au-glass au-pad" style={{marginBottom:20}}>
          <label style={S.label}>TARGET DOMAIN OR IP</label>
          <input
            className="au-input au-mono"
            placeholder="example.com  or  192.168.1.1"
            value={target}
            onChange={e => setTarget(e.target.value)}
            onKeyDown={e => e.key === "Enter" && !busy && requestScan()}
          />

          <label style={{...S.label, marginTop:18}}>SCAN TYPE</label>
          <div style={{display:"flex",gap:8,flexWrap:"wrap",marginBottom:22}}>
            {[["full","Full Scan"],["ssl","SSL Only"],["dns","DNS Only"],["port","Ports"],["headers","Headers"]].map(([v,l]) => (
              <button key={v} type="button" aria-pressed={scanType===v} onClick={() => setScanType(v)} style={{
                padding:"8px 16px",borderRadius:"var(--au-r-pill)",fontSize:12,fontWeight:700,cursor:"pointer",letterSpacing:1,
                background: scanType===v ? "var(--au-glass-strong)" : "transparent",
                border:"1px solid",
                borderColor: scanType===v ? "var(--au-accent)" : "var(--au-border)",
                color: scanType===v ? "var(--au-accent-ink)" : "var(--au-muted)"
              }}>{l}</button>
            ))}
          </div>
          <div style={{display:"flex",alignItems:"center",gap:8,padding:"11px 14px",marginBottom:18,border:"1px solid var(--au-border)",borderRadius:"var(--au-r-input)",background:"var(--au-glass)",fontSize:12.5,color:"var(--au-text-2)"}}>
            <Shield size={15} style={{color:"var(--au-accent-ink)",flex:"none"}}/>
            <span>A <strong style={{color:"var(--au-text)"}}>Full scan</strong> automatically runs the complete assessment suite — network, DNS, TLS, headers, tech &amp; CVE, web checks, subdomains &amp; more. No configuration needed.</span>
          </div>

          {/* ASSET CONTEXT — optional; sharpens risk scoring with business context */}
          <div style={{marginBottom:18,border:"1px solid var(--au-border)",borderRadius:"var(--au-r-input)",background:"var(--au-glass)",overflow:"hidden"}}>
            <button type="button" onClick={()=>setShowAssetCtx(v=>!v)} aria-expanded={showAssetCtx}
              style={{width:"100%",display:"flex",alignItems:"center",justifyContent:"space-between",gap:8,padding:"11px 14px",background:"transparent",border:"none",cursor:"pointer",color:"var(--au-text-2)",fontSize:12.5,textAlign:"left"}}>
              <span style={{display:"inline-flex",alignItems:"center",gap:8}}>
                <Layers size={15} style={{color:"var(--au-accent-ink)",flex:"none"}}/>
                Asset context <span style={{color:"var(--au-muted)"}}>— optional, sharpens risk scoring</span>
              </span>
              <span style={{color:"var(--au-muted)",fontSize:16,lineHeight:1}}>{showAssetCtx ? "−" : "+"}</span>
            </button>
            {showAssetCtx && (
              <div style={{padding:"4px 14px 14px"}}>
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:10}}>
                  {[["Asset criticality",assetCrit,setAssetCrit,[["","—"],["low","Low"],["medium","Medium"],["high","High"],["critical","Critical"]]],
                    ["Business impact",bizImpact,setBizImpact,[["","—"],["low","Low"],["medium","Medium"],["high","High"],["critical","Critical"]]],
                    ["Data classification",dataClass,setDataClass,[["","—"],["public","Public"],["internal","Internal"],["confidential","Confidential"],["regulated","Regulated"]]]
                  ].map(([lbl,val,setter,opts]) => (
                    <div key={lbl}>
                      <label style={{...S.label,fontSize:10}}>{lbl.toUpperCase()}</label>
                      <select value={val} onChange={e=>setter(e.target.value)} className="au-input" aria-label={lbl}>
                        {opts.map(([v,l]) => <option key={v} value={v}>{l}</option>)}
                      </select>
                    </div>
                  ))}
                </div>
                <div style={{...S.hint,marginTop:8}}>Tells the risk engine what this asset is worth. Left blank, scoring stays purely technical — nothing is assumed.</div>
              </div>
            )}
          </div>

          {/* ADVANCED — active pentest + authenticated testing. Collapsed by
              default: the only thing a first scan needs is a domain, and a
              red-bordered "sends live payloads" panel in the primary path reads
              as a warning about the product rather than an opt-in power tool. */}
          <div style={{marginBottom:18,border:"1px solid var(--au-border)",borderRadius:"var(--au-r-input)",background:"var(--au-glass)",overflow:"hidden"}}>
            <button type="button" onClick={()=>setShowAdvanced(v=>!v)} aria-expanded={showAdvanced}
              style={{width:"100%",display:"flex",alignItems:"center",justifyContent:"space-between",gap:8,padding:"11px 14px",background:"transparent",border:"none",cursor:"pointer",color:"var(--au-text-2)",fontSize:12.5,textAlign:"left"}}>
              <span style={{display:"inline-flex",alignItems:"center",gap:8}}>
                <KeyRound size={15} style={{color:"var(--au-accent-ink)",flex:"none"}}/>
                Advanced <span style={{color:"var(--au-muted)"}}>— active pentest, authenticated testing</span>
              </span>
              <span style={{color:"var(--au-muted)",fontSize:16,lineHeight:1}}>{showAdvanced ? "−" : "+"}</span>
            </button>
            {showAdvanced && (
            <div style={{padding:"4px 14px 14px"}}>
          {/* ACTIVE PENTEST (intrusive) — explicit authorization required */}
          <label style={{display:"flex",alignItems:"flex-start",gap:10,padding:"12px 14px",marginBottom:pentestMode?10:18,border:`1px solid ${pentestMode?"var(--au-error)":"var(--au-border)"}`,borderRadius:"var(--au-r-input)",background:pentestMode?"rgba(244,80,80,0.08)":"var(--au-glass)",cursor:"pointer"}}>
            <input type="checkbox" checked={pentestMode} onChange={e=>{setPentestMode(e.target.checked); if(!e.target.checked) setPentestAuth(false);}} style={{marginTop:2,accentColor:"var(--au-error)",width:16,height:16}}/>
            <span>
              <span style={{display:"inline-flex",alignItems:"center",gap:8,fontSize:13,fontWeight:700,color:pentestMode?"var(--au-error)":"var(--au-text-2)"}}><AlertTriangle size={15}/> Active pentest (intrusive)</span>
              <span style={{display:"block",fontSize:12,color:"var(--au-muted)",marginTop:3}}>Sends live payloads that <strong>confirm</strong> SQL-injection, command-injection &amp; XSS + bounded write-probes (mass-assignment, default-credentials, file-upload). Higher-impact — only on systems you control.</span>
            </span>
          </label>
          {pentestMode && (
            <label style={{display:"flex",alignItems:"flex-start",gap:10,padding:"10px 14px",marginBottom:12,border:"1px solid var(--au-error)",borderRadius:"var(--au-r-input)",cursor:"pointer"}}>
              <input type="checkbox" checked={pentestAuth} onChange={e=>setPentestAuth(e.target.checked)} style={{marginTop:2,accentColor:"var(--au-error)",width:16,height:16}}/>
              <span style={{fontSize:12.5,color:"var(--au-text-2)",lineHeight:1.6}}>I confirm I <strong>own or am explicitly authorized</strong> to perform active/intrusive security testing on <strong style={{color:"var(--au-accent-ink)"}}>{target||"this target"}</strong>. This action is logged.</span>
            </label>
          )}
          {pentestMode && (
            <div style={{marginBottom:18,padding:"14px 16px",border:"1px solid var(--au-hair)",borderRadius:"var(--au-r-input)",background:"var(--au-glass)"}}>
              <div style={{...S.label,marginBottom:8,display:"flex",alignItems:"center",gap:6}}><KeyRound size={13}/> AUTHENTICATED TESTING <span style={{fontWeight:400,textTransform:"none",color:"var(--au-muted)"}}>— optional; tests behind login (BOLA/IDOR, data exposure)</span></div>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
                <div><label style={{...S.label,fontSize:10}}>SESSION COOKIE</label><input value={authCookie} onChange={e=>setAuthCookie(e.target.value)} placeholder="session=abc123; other=…" className="au-input au-mono" autoComplete="off"/></div>
                <div><label style={{...S.label,fontSize:10}}>BEARER TOKEN</label><input type="password" value={authBearer} onChange={e=>setAuthBearer(e.target.value)} placeholder="eyJhbGciOi…" className="au-input au-mono" autoComplete="new-password"/></div>
              </div>
              <div style={{...S.hint,marginTop:8}}>Credentials are used only for this scan and never stored.</div>
            </div>
          )}

            </div>
            )}
          </div>

          <button onClick={requestScan} disabled={busy} className="au-pill au-pill-primary">
            {busy ? "Scanning…" : <>Start Scan <ArrowRight size={16}/></>}
          </button>
        </div>

        <ScheduledScansPanel target={target} scanType={scanType} />

        {busy && (
          <div className="au-glass au-pad" style={{marginBottom:20}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
              <span className="au-mono" style={{color:"var(--au-accent-ink)",fontSize:13}}>{phase}</span>
              <span className="au-mono" style={{color:"var(--au-muted)",fontSize:13}}>{progress}%</span>
            </div>
            <div style={{background:"var(--au-glass-strong)",borderRadius:3,height:4,overflow:"hidden"}}>
              <div style={{height:"100%",width:`${progress}%`,background:"var(--au-accent)",borderRadius:3,transition:"width 0.5s ease"}}/>
            </div>
          </div>
        )}

        {result && (
          <>
            {/* RISK SCORE CARD */}
            <div className="au-glass au-pad" style={{marginBottom:20}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:24,flexWrap:"wrap",gap:12}}>
                <div>
                  <div className="au-stat-lbl" style={{letterSpacing:2,marginBottom:6}}>RISK SCORE — {result.target}</div>
                  <div className="au-stat-num au-mono" style={{fontSize:56,fontWeight:700,color:rc,lineHeight:1}}>{result.risk_score}</div>
                  <div style={{fontSize:12,color:"var(--au-muted)",marginTop:4}}>/100 — {result.risk_score>=70?"Critical":result.risk_score>=40?"Moderate":"Low"} Risk</div>
                </div>
                <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
                  {["pdf","pdf-standard","html","csv","docx"].map(fmt => {
                    const permKey = fmt === "pdf-standard" ? "pdf" : fmt;
                    const allowed = planInfo.downloads?.includes(permKey);
                    const label = fmt === "pdf" ? "PDF (Pageless)" : fmt === "pdf-standard" ? "PDF (Paged)" : fmt.toUpperCase();
                    return (
                      <button key={fmt} type="button" onClick={() => download(fmt)}
                        aria-disabled={!allowed}
                        aria-label={allowed ? `Download ${label}` : `Upgrade required to download ${label}`}
                        style={{...S.btnDl, opacity: allowed ? 1 : 0.4, cursor: allowed ? "pointer" : "not-allowed"}}
                        title={allowed ? `Download ${label}` : `Upgrade required for ${label}`}>
                        {allowed ? <Download size={13}/> : <Lock size={13}/>} {label}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* PLAN BADGE */}
              <div style={S.planBadge}>
                <div>
                  <span style={{color:"var(--au-accent-ink)",fontSize:11,letterSpacing:2,fontWeight:700}}>YOUR PLAN: </span>
                  <span style={{color:"var(--au-text)",fontWeight:700,fontSize:14}}>{(planInfo.current || "FREE").toUpperCase()}</span>
                </div>
                <div style={{color:"var(--au-muted)",fontSize:12}}>
                  {planInfo.quota_period === "credits"
                    ? "pay-per-scan (Entry)"
                    : (planInfo.scans_remaining != null && planInfo.quota_period
                        ? `${planInfo.scans_remaining} scans left this ${planInfo.quota_period}`
                        : "")}
                </div>
              </div>
            </div>

            {/* IP INTELLIGENCE — NEW */}
            <IPIntelligenceCard data={result.raw_results?.ip_intelligence} />

            {/* RECON SUITE — subdomain, tech stack, WAF */}
            <ReconCard
              subdomains={result.raw_results?.subdomains}
              techStack={result.raw_results?.tech_stack}
              waf={result.raw_results?.waf}
            />

            {/* CVE Vulnerabilities */}
            <CVECard data={result.raw_results?.cves} />

            {/* Target Screenshot - Visual Evidence */}
            <ScreenshotCard
              scanId={result.scan_id}
              available={result.raw_results?.screenshot?.available && !result.raw_results?.screenshot?.error}
            />

            {/* RISK BREAKDOWN */}
            <RiskBreakdown
              breakdown={result.raw_results?._risk_breakdown}
              verificationId={result.raw_results?._verification_id}
            />

            {/* SSL SECTION */}
            <ScanSection title="SSL / TLS Certificate" icon={KeyRound} data={result.raw_results?.ssl} />

            {/* DNS SECTION */}
            <ScanSection title="DNS Records" icon={Globe} data={result.raw_results?.dns} />

            {/* HEADERS SECTION */}
            <HeadersSection data={result.raw_results?.headers} />

            {/* PORTS SECTION */}
            <PortsSection data={result.raw_results?.ports} />

            {/* CRITICAL & HIGH LOCK NOTICE */}
            {planInfo.critical_high_locked && (
              <div style={S.upgradeBanner}>
                <div>
                  <div style={{fontSize:16,fontWeight:700,color:"var(--au-warn)",marginBottom:6,display:"flex",alignItems:"center",gap:8}}><Lock size={16}/> Critical &amp; High details locked</div>
                  <div style={{fontSize:13,color:"var(--au-muted)"}}>Unlock with Entry ($5)</div>
                </div>
                <Link to="/#pricing" className="au-pill au-pill-primary">Unlock with Entry <ArrowRight size={16}/></Link>
              </div>
            )}

            {/* AI ANALYSIS */}
            <div className="au-glass au-pad" style={{marginBottom:20}}>
              <div style={{fontSize:11,color:"var(--au-accent-ink)",letterSpacing:2,marginBottom:12,fontWeight:700,display:"flex",alignItems:"center",gap:8}}><Bot size={14}/> SECURITY ANALYSIS</div>
              {planInfo.show_ai === false ? (
                <div style={S.lockedFull}>
                  <Lock size={36} style={{color:"var(--au-warn)",marginBottom:14}}/>
                  <div style={{fontSize:16,fontWeight:700,color:"var(--au-warn)",marginBottom:8}}>AI Analysis Locked</div>
                  <div style={{fontSize:13,color:"var(--au-muted)",marginBottom:16}}>Upgrade to Starter plan or higher for full AI-powered security analysis</div>
                  <Link to="/#pricing" className="au-pill au-pill-primary au-pill-sm">Upgrade Now <ArrowRight size={14}/></Link>
                </div>
              ) : (
                <AiReport text={result.ai_report} />
              )}
            </div>

            {/* DEEP TOOLS — Nikto / Lynis terminal output */}
            {result.raw_results?.nikto && <TerminalCard title="Nikto — Web Vulnerability Scan" data={result.raw_results.nikto} scanId={result.scan_id} suffix="nikto" meta={`${result.raw_results.nikto.item_count ?? 0} findings · ${result.raw_results.nikto.duration_s ?? "?"}s`}/>}
            {result.raw_results?.lynis && <TerminalCard title="Lynis — Host Security Audit" data={result.raw_results.lynis} scanId={result.scan_id} suffix="lynis" meta={result.raw_results.lynis.hardening_index!=null?`Hardening index ${result.raw_results.lynis.hardening_index}/100`:""} note={result.raw_results.lynis.scope}/>}

            {/* AI SECURITY AGENT — report-aware chat (Module 4) */}
            {planInfo.show_ai !== false && result.scan_id && (
              <ScanAgentChat scanId={result.scan_id} />
            )}

            {/* UPGRADE BANNER */}
            {planInfo.tier_message && (
              <div style={S.upgradeBanner}>
                <div>
                  <div style={{fontSize:18,fontWeight:700,color:"var(--au-text)",marginBottom:6,display:"flex",alignItems:"center",gap:8}}><Gem size={18} style={{color:"var(--au-accent-ink)"}}/> Unlock More Insights</div>
                  <div style={{fontSize:13,color:"var(--au-muted)"}}>{planInfo.tier_message}</div>
                </div>
                <Link to="/#pricing" className="au-pill au-pill-primary">Upgrade Now <ArrowRight size={16}/></Link>
              </div>
            )}

            {/* HONEST DISCLAIMER */}
            <div style={S.disclaimerNote}>
              <AlertTriangle size={14} style={{color:"var(--au-warn)",flex:"none",marginTop:2}}/>
              <span><strong style={{color:"var(--au-warn)"}}>Disclaimer: </strong> Aegis is an AI-powered tool. Like all scanners, results may contain errors. Findings are guidance, not definitive. Always verify independently for critical decisions.</span>
            </div>
          </>
        )}
      </div>


      {/* DISCLAIMER MODAL */}
      {showDisclaimer && (
        <div style={S.overlay} onClick={(e) => e.target === e.currentTarget && setShowDisclaimer(false)}>
          <div className="au-glass" style={S.modal}>
            <div style={S.modalHead}>
              <Scale size={22} style={{color:"var(--au-accent-ink)",marginRight:10}}/>
              <span style={{fontSize:18,fontWeight:700,color:"var(--au-text)"}}>Legal Authorization Required</span>
            </div>
            <div style={S.disclaimerBox}>
              <pre style={{fontSize:12,color:"var(--au-text-2)",lineHeight:1.8,whiteSpace:"pre-wrap",fontFamily:"var(--au-font)",margin:0}}>{disclaimerText}</pre>
            </div>
            <div style={{display:"flex",alignItems:"flex-start",gap:10,marginBottom:20}}>
              <input type="checkbox" id="agree" checked={agreed} onChange={e => setAgreed(e.target.checked)} style={{marginTop:4,cursor:"pointer",width:18,height:18,accentColor:"var(--au-accent)"}}/>
              <label htmlFor="agree" style={{fontSize:13,color:"var(--au-text-2)",cursor:"pointer",lineHeight:1.6}}>
                I have read, understood, and agree to all terms above. I confirm I have authorization to scan <strong style={{color:"var(--au-accent-ink)"}}>{target}</strong>
              </label>
            </div>
            <div style={{display:"flex",gap:10,justifyContent:"flex-end"}}>
              <button onClick={() => setShowDisclaimer(false)} className="au-pill au-pill-glass au-pill-sm">Cancel</button>
              <button onClick={startScan} disabled={!agreed} className="au-pill au-pill-primary au-pill-sm" style={{opacity: agreed ? 1 : 0.4, cursor: agreed ? "pointer" : "not-allowed"}}>
                I Agree & Start Scan <ArrowRight size={14}/>
              </button>
            </div>
          </div>
        </div>
      )}

      <CompanyVerifyModal
        open={showCompanyVerify}
        onClose={() => setShowCompanyVerify(false)}
        onVerified={() => { setShowCompanyVerify(false); startScan(); }}
      />
    </div>
  );
}

// SCHEDULED SCANS — Pro/Max recurring scans panel
function fmtRun(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return String(iso);
  return d.toLocaleString(undefined, {
    month: "short", day: "numeric", hour: "2-digit", minute: "2-digit",
  });
}

function ScheduledScansPanel({ target, scanType }) {
  const [schedules, setSchedules] = useState([]);
  const [allowed, setAllowed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [frequency, setFrequency] = useState("daily");
  const [creating, setCreating] = useState(false);

  const load = async () => {
    try {
      const r = await api.get("/api/scan/schedules");
      setSchedules(Array.isArray(r.data?.schedules) ? r.data.schedules : []);
      setAllowed(!!r.data?.allowed);
    } catch {
      setAllowed(false);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!target.trim()) { toast.error("Enter a target above first"); return; }
    setCreating(true);
    try {
      await api.post("/api/scan/schedules", { target: target.trim(), scan_type: scanType, frequency });
      toast.success("Scan scheduled");
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not schedule scan");
    } finally {
      setCreating(false);
    }
  };

  const toggle = async (s) => {
    try {
      await api.patch(`/api/scan/schedules/${s.id}`, { active: !s.active });
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not update schedule");
    }
  };

  const remove = async (s) => {
    try {
      await api.delete(`/api/scan/schedules/${s.id}`);
      toast.success("Schedule removed");
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not delete schedule");
    }
  };

  if (loading) return null;

  if (!allowed) {
    return (
      <div className="au-glass au-pad" style={{ marginBottom: 20 }}>
        <div style={{ fontSize: 11, color: "var(--au-accent-ink)", letterSpacing: 2, marginBottom: 12, fontWeight: 700, display: "flex", alignItems: "center", gap: 8 }}>
          <CalendarClock size={14} /> AUTOMATED SCANS
        </div>
        <div style={{ fontSize: 13, color: "var(--au-muted)", marginBottom: 16 }}>
          Schedule recurring scans on Pro &amp; Max.
        </div>
        <Link to="/#pricing" className="au-pill au-pill-primary au-pill-sm">Upgrade Now <ArrowRight size={14} /></Link>
      </div>
    );
  }

  return (
    <div className="au-glass au-pad" style={{ marginBottom: 20 }}>
      <div style={{ fontSize: 11, color: "var(--au-accent-ink)", letterSpacing: 2, marginBottom: 14, fontWeight: 700, display: "flex", alignItems: "center", gap: 8 }}>
        <CalendarClock size={14} /> SCHEDULED SCANS
      </div>

      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center", marginBottom: 18 }}>
        <select
          className="au-input au-mono"
          value={frequency}
          onChange={(e) => setFrequency(e.target.value)}
          style={{ width: "auto", minWidth: 140 }}
        >
          <option value="daily">Daily</option>
          <option value="weekly">Weekly</option>
          <option value="monthly">Monthly</option>
        </select>
        <button onClick={create} disabled={creating} className="au-pill au-pill-primary au-pill-sm">
          <Plus size={14} /> {creating ? "Scheduling…" : "Schedule current target"}
        </button>
      </div>

      {schedules.length === 0 ? (
        <div style={{ color: "var(--au-muted)", fontSize: 13 }}>No scheduled scans yet.</div>
      ) : (
        <div style={{ display: "grid", gap: 10 }}>
          {schedules.map((s) => (
            <div key={s.id} style={S.scheduleRow}>
              <div style={{ display: "flex", flexDirection: "column", gap: 4, minWidth: 0, flex: 1 }}>
                <span className="au-mono" style={{ color: "var(--au-text)", fontSize: 13, wordBreak: "break-all" }}>{s.target}</span>
                <span style={{ fontSize: 12, color: "var(--au-muted)" }}>
                  <span style={{ textTransform: "capitalize" }}>{s.frequency}</span>
                  {" · "}{s.scan_type}
                  {" · next "}{fmtRun(s.next_run_at)}
                </span>
              </div>
              <div style={{ display: "flex", gap: 8, flex: "none" }}>
                <button
                  onClick={() => toggle(s)}
                  className="au-pill au-pill-glass au-pill-sm"
                  title={s.active ? "Pause schedule" : "Resume schedule"}
                  style={{ color: s.active ? "var(--au-accent-ink)" : "var(--au-muted)" }}
                >
                  <Power size={13} /> {s.active ? "Active" : "Paused"}
                </button>
                <button
                  onClick={() => remove(s)}
                  className="au-pill au-pill-glass au-pill-sm"
                  title="Delete schedule"
                  aria-label={`Delete schedule for ${s.target}`}
                  style={{ color: "var(--au-error)" }}
                >
                  <Trash2 size={13} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// SCAN SECTION COMPONENT
// SECURITY HEADERS — present/missing/cookies as pills
function hPill(tone) {
  const map = {
    good: { bg: "rgba(31,208,163,0.12)", bd: "var(--au-accent)", fg: "var(--au-accent-ink)" },
    bad: { bg: "rgba(244,80,80,0.10)", bd: "rgba(244,80,80,0.45)", fg: "var(--au-error)" },
    neutral: { bg: "var(--au-glass)", bd: "var(--au-border)", fg: "var(--au-muted)" },
  }[tone];
  return { display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12.5, padding: "5px 12px", borderRadius: "var(--au-r-pill)", background: map.bg, border: `1px solid ${map.bd}`, color: map.fg, wordBreak: "break-word" };
}
const hSub = { margin: "16px 0 9px", fontSize: 11, letterSpacing: 1.3, textTransform: "uppercase", color: "var(--au-muted)", fontWeight: 700 };

function HeadersSection({ data }) {
  if (!data || typeof data !== "object") return null;
  const present = Array.isArray(data.present) ? data.present : [];
  const missing = Array.isArray(data.missing) ? data.missing : [];
  const cookies = data.cookies && typeof data.cookies === "object" ? data.cookies : null;
  const lockedKeys = data._locked || data._locked_fields || [];
  const lockedCount = data._locked_count || lockedKeys.length;
  const upgradeMsg = data._upgrade_msg;
  const total = present.length + missing.length;
  return (
    <div className="au-glass au-pad" style={{ marginBottom: 20 }}>
      <div style={{ fontSize: 11, color: "var(--au-accent-ink)", letterSpacing: 2, marginBottom: 4, fontWeight: 700, display: "flex", alignItems: "center", gap: 8 }}>
        <ClipboardList size={14} /> SECURITY HEADERS
        {total > 0 && <span style={{ marginLeft: "auto", color: present.length === total ? "var(--au-success)" : "var(--au-warn)", letterSpacing: 0, fontSize: 12 }}>{present.length}/{total} present</span>}
      </div>
      {present.length > 0 && (<>
        <div style={hSub}>Present</div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>{present.map((h) => <span key={h} style={hPill("good")}><CheckCircle2 size={12} />{h}</span>)}</div>
      </>)}
      {missing.length > 0 && (<>
        <div style={hSub}>Missing</div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>{missing.map((h) => <span key={h} style={hPill("bad")}><XCircle size={12} />{h}</span>)}</div>
      </>)}
      {cookies && (<>
        <div style={hSub}>Cookies</div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
          {["present", "secure", "httponly", "samesite"].map((f) => {
            const on = !!cookies[f];
            return <span key={f} style={hPill(on ? "good" : "neutral")}>{on ? <CheckCircle2 size={12} /> : <XCircle size={12} />}{f}</span>;
          })}
        </div>
      </>)}
      {lockedKeys.length > 0 && (<>
        <div style={hSub}>Locked</div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>{lockedKeys.map((k) => <span key={k} style={{ ...hPill("neutral"), color: "var(--au-warn)" }}><Lock size={11} />{String(k)}</span>)}</div>
      </>)}
      {lockedCount > 0 && !lockedKeys.length && (
        <div style={{ marginTop: 14, textAlign: "center", color: "var(--au-warn)", fontSize: 13 }}>
          <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><Lock size={13} /> <strong>{lockedCount} more field{lockedCount > 1 ? "s" : ""} locked</strong></span>
          {upgradeMsg && <div style={{ fontSize: 12, marginTop: 4, color: "var(--au-muted)" }}>{upgradeMsg}</div>}
        </div>
      )}
      {total === 0 && !cookies && <div style={{ color: "var(--au-muted)", fontSize: 13 }}>No header data captured.</div>}
    </div>
  );
}

// AI report — lightweight markdown (headings / bold / code / bullets / rules)
function aiInline(s) {
  const out = []; const re = /(\*\*[^*]+\*\*|`[^`]+`)/g; let last = 0, m, i = 0;
  while ((m = re.exec(s))) {
    if (m.index > last) out.push(s.slice(last, m.index));
    const t = m[0];
    if (t.startsWith("**")) out.push(<strong key={i++} style={{ color: "var(--au-text)" }}>{t.slice(2, -2)}</strong>);
    else out.push(<code key={i++} style={{ fontFamily: "var(--au-mono, monospace)", color: "var(--au-accent-ink)", background: "var(--au-glass)", border: "1px solid var(--au-border)", padding: "1px 6px", borderRadius: 4, fontSize: "0.9em" }}>{t.slice(1, -1)}</code>);
    last = m.index + t.length;
  }
  if (last < s.length) out.push(s.slice(last));
  return out;
}
function AiReport({ text }) {
  if (!text) return <div style={{ color: "var(--au-muted)", fontSize: 13 }}>No AI report available.</div>;
  const blocks = []; let list = [];
  const flush = (k) => { if (list.length) { blocks.push(<ul key={"u" + k} style={{ margin: "4px 0 10px", paddingLeft: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: 7 }}>{list}</ul>); list = []; } };
  text.split("\n").forEach((raw, i) => {
    const line = raw.trim();
    if (!line) { flush(i); return; }
    if (/^---+$/.test(line)) { flush(i); blocks.push(<hr key={i} style={{ border: "none", borderTop: "1px solid var(--au-hair)", margin: "16px 0" }} />); return; }
    if (line.startsWith("## ")) { flush(i); blocks.push(<h3 key={i} style={{ fontSize: 15, fontWeight: 700, color: "var(--au-text)", letterSpacing: 1, margin: "18px 0 8px" }}>{aiInline(line.slice(3))}</h3>); return; }
    if (line.startsWith("### ")) { flush(i); blocks.push(<h4 key={i} style={{ fontSize: 13.5, fontWeight: 700, color: "var(--au-text)", margin: "14px 0 6px" }}>{aiInline(line.slice(4))}</h4>); return; }
    if (line.startsWith("- ") || line.startsWith("* ")) { list.push(<li key={i} style={{ display: "flex", gap: 9, fontSize: 14, color: "var(--au-text-2)", lineHeight: 1.6 }}><span style={{ color: "var(--au-accent-ink)", flex: "none" }}>▸</span><span>{aiInline(line.slice(2))}</span></li>); return; }
    flush(i);
    blocks.push(<p key={i} style={{ fontSize: 14, color: "var(--au-text-2)", lineHeight: 1.7, margin: "0 0 8px" }}>{aiInline(line)}</p>);
  });
  flush("end");
  return <div>{blocks}</div>;
}

function ScanSection({ title, icon: Icon, data }) {
  if (!data || typeof data !== 'object') return null;

  const items = Object.entries(data).filter(([k]) => !k.startsWith('_'));
  const lockedKeys = data._locked || data._locked_fields || [];
  const lockedCount = data._locked_count || lockedKeys.length;
  const upgradeMsg = data._upgrade_msg;

  return (
    <div className="au-glass au-pad" style={{marginBottom:20}}>
      <div style={{fontSize:11,color:"var(--au-accent-ink)",letterSpacing:2,marginBottom:14,fontWeight:700,display:"flex",alignItems:"center",gap:8}}>{Icon && <Icon size={14}/>} {title}</div>
      <div style={{display:"grid",gap:10}}>
        {items.map(([key, val]) => (
          <div key={key} style={S.fieldRow}>
            <span style={S.fieldKey}>{key.toUpperCase()}</span>
            {(() => {
              const full = typeof val === 'object' ? JSON.stringify(val) : String(val);
              const lim = typeof val === 'object' ? 100 : 200;
              const shown = full.length > lim ? full.substring(0, lim) + "…" : (full || "—");
              return <span className="au-mono" style={S.fieldVal} title={full}>{shown}</span>;
            })()}
          </div>
        ))}
        {lockedKeys.map(key => (
          <div key={key} style={{...S.fieldRow, opacity: 0.6}}>
            <span style={S.fieldKey}>{String(key).toUpperCase()}</span>
            <span className="au-mono" style={{...S.fieldVal, color:"var(--au-warn)",display:"inline-flex",alignItems:"center",gap:6,justifyContent:"flex-end"}}><Lock size={12}/> [Upgrade to view]</span>
          </div>
        ))}
        {lockedCount > 0 && !lockedKeys.length && (
          <div style={S.lockedHint}>
            <span style={{display:"inline-flex",alignItems:"center",gap:6,justifyContent:"center"}}><Lock size={13}/> <strong>{lockedCount} more field{lockedCount > 1 ? 's' : ''} locked</strong></span>
            {upgradeMsg && <div style={{fontSize:12,marginTop:4,color:"var(--au-muted)"}}>{upgradeMsg}</div>}
          </div>
        )}
      </div>
    </div>
  );
}

// PORTS SECTION
function PortsSection({ data }) {
  if (!data) return null;

  let portsList = [];
  let lockedCount = 0;
  let upgradeMsg = null;

  if (Array.isArray(data)) {
    portsList = data;
  } else if (data.shown) {
    portsList = data.shown;
    lockedCount = data._locked_count || 0;
    upgradeMsg = data.upgrade_msg;
  }

  return (
    <div className="au-glass au-pad" style={{marginBottom:20}}>
      <div style={{fontSize:11,color:"var(--au-accent-ink)",letterSpacing:2,marginBottom:14,fontWeight:700,display:"flex",alignItems:"center",gap:8}}><Plug size={14}/> Open Ports</div>
      {portsList.length === 0 ? (
        <div style={{color:"var(--au-muted)",fontSize:13}}>No open ports detected</div>
      ) : (
        <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(140px,1fr))",gap:10}}>
          {portsList.map((p, i) => (
            <div key={i} style={S.portCard}>
              <div className="au-mono" style={{color:"var(--au-accent-ink)",fontSize:14,fontWeight:700}}>{typeof p === 'object' ? p.port : p}</div>
              {typeof p === 'object' && p.service && <div style={{fontSize:11,color:"var(--au-muted)"}}>{p.service}</div>}
            </div>
          ))}
        </div>
      )}
      {lockedCount > 0 && (
        <div style={{...S.lockedHint, marginTop:14}}>
          <span style={{display:"inline-flex",alignItems:"center",gap:6,justifyContent:"center"}}><Lock size={13}/> <strong>{lockedCount} more port{lockedCount > 1 ? 's' : ''} locked</strong></span>
          {upgradeMsg && <div style={{fontSize:12,marginTop:4,color:"var(--au-muted)"}}>{upgradeMsg}</div>}
        </div>
      )}
    </div>
  );
}

// TERMINAL CARD — renders raw tool output (Nikto/Lynis) in a terminal UI
function TerminalCard({ title, data, meta, note }) {
  if (!data) return null;
  const raw = data.raw || (data.error ? `Error: ${data.error}` : (data.note || "(no output)"));
  const cmd = data.command || "";
  return (
    <div className="au-glass au-pad" style={{ marginBottom: 20 }}>
      <div style={{ fontSize: 11, color: "var(--au-accent-ink)", letterSpacing: 2, marginBottom: 12, fontWeight: 700, display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
        <Terminal size={14} /> {title}
        {meta && <span style={{ marginLeft: "auto", color: "var(--au-muted)", letterSpacing: 0, fontSize: 12 }}>{meta}</span>}
      </div>
      {note && <div style={{ fontSize: 12, color: "var(--au-warn)", marginBottom: 10, fontStyle: "italic" }}>{note}</div>}
      <div style={{ background: "#0d1117", border: "1px solid #1c2b24", borderRadius: 10, overflow: "hidden" }}>
        <div style={{ background: "#131b17", padding: "10px 14px", display: "flex", alignItems: "center", gap: 8, borderBottom: "1px solid #1c2b24" }}>
          <span style={{ width: 11, height: 11, borderRadius: "50%", background: "#ff5f56" }} />
          <span style={{ width: 11, height: 11, borderRadius: "50%", background: "#ffbd2e" }} />
          <span style={{ width: 11, height: 11, borderRadius: "50%", background: "#27c93f" }} />
          <span style={{ marginLeft: 8, fontSize: 12, color: "#7d8b85", fontFamily: "var(--au-font-mono)" }}>{title}</span>
        </div>
        <pre className="au-mono" style={{ margin: 0, padding: "14px 16px", color: "#c8d3cc", fontFamily: "var(--au-font-mono)", fontSize: 12, lineHeight: 1.6, whiteSpace: "pre-wrap", wordBreak: "break-word", maxHeight: 440, overflow: "auto" }}>
          <span style={{ color: "#1FD0A3", fontWeight: 700 }}>aegis@scanner</span>:<span style={{ color: "#5aa0ff" }}>~</span>$ {cmd}{"\n\n"}{raw}
        </pre>
      </div>
    </div>
  );
}

const S = {
  nav: {padding:"14px 28px",display:"flex",alignItems:"center",justifyContent:"space-between"},
  label: {display:"block",fontSize:11,color:"var(--au-muted)",letterSpacing:2,textTransform:"uppercase",marginBottom:8},
  btnDl: {background:"var(--au-glass-strong)",border:"1px solid var(--au-border)",color:"var(--au-accent-ink)",padding:"9px 14px",borderRadius:"var(--au-r-pill)",fontSize:11,fontWeight:700,letterSpacing:1,display:"inline-flex",alignItems:"center",gap:6},
  planBadge: {display:"flex",justifyContent:"space-between",alignItems:"center",padding:"12px 16px",background:"var(--au-glass)",border:"1px solid var(--au-border)",borderRadius:"var(--au-r-input)",marginTop:8},
  fieldRow: {display:"flex",justifyContent:"space-between",padding:"10px 14px",background:"var(--au-glass)",borderRadius:"var(--au-r-input)",gap:14,flexWrap:"wrap"},
  fieldKey: {color:"var(--au-muted)",fontSize:12,letterSpacing:1,fontWeight:600,minWidth:100},
  fieldVal: {color:"var(--au-text)",fontSize:13,wordBreak:"break-all",textAlign:"right",flex:1},
  lockedHint: {background:"var(--au-glass)",border:"1px dashed var(--au-border)",borderRadius:"var(--au-r-input)",padding:"14px 18px",color:"var(--au-warn)",fontSize:13,textAlign:"center",marginTop:10},
  lockedFull: {textAlign:"center",padding:"40px 24px",background:"var(--au-glass)",border:"1px dashed var(--au-border)",borderRadius:"var(--au-r-sm)"},
  upgradeBanner: {background:"var(--au-glass)",border:"1px solid var(--au-border)",borderRadius:"var(--au-r-card)",padding:"24px 28px",display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:16,marginBottom:20},
  portCard: {background:"var(--au-glass)",border:"1px solid var(--au-border)",borderRadius:"var(--au-r-input)",padding:"12px",textAlign:"center"},
  scheduleRow: {display:"flex",justifyContent:"space-between",alignItems:"center",gap:14,padding:"12px 14px",background:"var(--au-glass)",border:"1px solid var(--au-border)",borderRadius:"var(--au-r-input)",flexWrap:"wrap"},
  disclaimerNote: {background:"var(--au-glass)",border:"1px solid var(--au-border)",borderRadius:"var(--au-r-input)",padding:"14px 18px",fontSize:12,color:"var(--au-muted)",lineHeight:1.7,marginTop:14,display:"flex",alignItems:"flex-start",gap:10},
  overlay: {position:"fixed",inset:0,background:"rgba(0,0,0,0.85)",backdropFilter:"blur(8px)",zIndex:1000,display:"flex",alignItems:"center",justifyContent:"center",padding:20},
  modal: {padding:28,maxWidth:600,width:"100%",maxHeight:"90vh",display:"flex",flexDirection:"column"},
  modalHead: {display:"flex",alignItems:"center",marginBottom:18,paddingBottom:14,borderBottom:"1px solid var(--au-hair)"},
  disclaimerBox: {background:"var(--au-glass)",border:"1px solid var(--au-border)",borderRadius:"var(--au-r-input)",padding:18,marginBottom:18,maxHeight:300,overflowY:"auto"},
};
